/**
 * Resolves a stored image URL to a fully-qualified URL ready for <img src=...>.
 *
 * Why this exists:
 *   Uploaded product images are now stored as relative paths (`/api/images/{id}`)
 *   in MongoDB so they survive preview/production hostname rotations and pod
 *   redeployments. Anywhere we render product.image, we route it through this
 *   helper so the browser fetches it from the currently-active backend host.
 *
 *   Pass-through behaviour:
 *     • Empty / null    → returns ''
 *     • Already absolute (http://, https://, data:, blob:) → returned untouched
 *     • Starts with '/' → prepended with REACT_APP_BACKEND_URL
 *     • Anything else (legacy raw filename) → treated as relative & prepended
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Optionally append a cache-buster (`?v={updated_at}` or a version hash)
 * so the browser refetches whenever the underlying image record is
 * updated in the DB. Callers pass the product's `updated_at` timestamp
 * (or any monotonically-changing value) as `version` — the resolver
 * only tacks it on when the URL doesn't already carry a query string.
 */
export const resolveImageUrl = (url, version) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  // Absolute URLs (incl. data: and blob:) — return as-is so external CDN images keep working
  let out;
  if (/^(https?:|data:|blob:)/i.test(url)) {
    out = url;
  } else if (url.startsWith('/')) {
    // Relative path — prepend the active backend host
    out = `${API_URL}${url}`;
  } else {
    // Bare filename fallback
    out = `${API_URL}/${url}`;
  }
  if (version && !out.includes('?')) {
    // Compact version: last 10 chars of the timestamp/hash is enough to
    // change on every DB write without polluting the URL.
    const v = encodeURIComponent(String(version).slice(-10));
    out = `${out}?v=${v}`;
  }
  return out;
};

// Responsive, compressed image props. `/api/images/{id}` → resized WebP variants
// (backend `?w=&fmt=webp`, originals untouched); shipped `/images/blog/*.png` → `.webp` twin.
// Anything else passes through unchanged.
const VARIANT_WIDTHS = [160, 320, 480, 640, 960];

export const optimizedImageProps = (url, { version, sizes, maxWidth = 960 } = {}) => {
  const src = resolveImageUrl(url, version);
  if (!src) return { src: '' };
  const apiImage = src.match(/^(.*\/api\/images\/[A-Za-z0-9-]+)(?:\?.*)?$/);
  if (apiImage) {
    const base = apiImage[1];
    const widths = VARIANT_WIDTHS.filter((w) => w <= maxWidth);
    const at = (w) => `${base}?w=${w}&fmt=webp`;
    return {
      src: at(widths.find((w) => w >= Math.min(480, maxWidth)) || widths[widths.length - 1]),
      srcSet: widths.map((w) => `${at(w)} ${w}w`).join(', '),
      sizes,
    };
  }
  if (/\/images\/blog\/[^/?]+\.png$/i.test(src)) return { src: src.replace(/\.png$/i, '.webp') };
  return { src };
};
